/*
 * window.SATPhotoPicker — picks the "shows the item" photo out of a
 * product's full image set, instead of always trusting whichever image
 * Shopify happens to call featured_image.
 *
 * Oscar4Fox in particular lists the same hooded combat shirt across several
 * patterns, and a merchant uploading those often puts a lifestyle photo
 * (someone wearing it, outdoors, mid-motion) first and a flat/mannequin
 * studio shot second or third — great marketing photo, bad for a pattern
 * swatch rail where the point is judging the fabric print itself. There's
 * no Shopify field that says "this one is the plain shot" to read directly,
 * so this scores each image instead: sample a handful of patches (the four
 * corners plus top-center, where a seamless studio background sits and a
 * worn/lifestyle photo almost never does) and look for the bright, low-
 * variance signature of an actual seamless backdrop. A convincing winner
 * replaces the default; anything ambiguous (patterned background, another
 * product shot with genuinely nothing plain about it) just leaves the
 * merchant's own featured_image in place rather than guessing.
 *
 * Scoring requires the image pixels to actually be sampled, which requires
 * the image to load — this is inherently async. choose() always returns the
 * safe default (product.photo) synchronously for first paint, and calls
 * onBetter(url) later, from a promise callback, only if a real winner turns
 * up; callers re-render off that callback. Every product is scored at most
 * once per page load — results are cached by product id in memory.
 *
 * That cache used to only get written when a confident winner turned up
 * (`if (chosen !== fallback) { cache[id] = chosen; ... }`) — a product whose
 * photos scored too ambiguously to override the merchant's own choice never
 * got a cache entry at all, so `choose()` treated it as never-attempted and
 * re-ran the entire scoring pass (load every image, sample canvas pixels
 * five times each) on every single call. Harmless-looking at a glance since
 * each individual call is cheap and async, but callers run choose() once per
 * product every renderVals — with renders now firing as often as they're
 * meant to (see assets/sat-render-watchdog-patch.js), that turned into a
 * genuine runaway loop for any product with an ambiguous result: thousands
 * of canvas reads a second, pegging the main thread hard enough to look like
 * the exact "janky, out of sync" symptom reported against unrelated
 * animations elsewhere on the page. Fixed by caching every outcome —
 * ambiguous included — the first time a product is actually scored.
 */
(function () {
  'use strict';

  var cache = {}; // productId -> chosen url, once scored (fallback counts as a real answer, not "unscored")
  var pending = {}; // productId -> true while a scoring pass is in flight

  function loadImg(url) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous'; // Shopify's CDN sends CORS headers; without this a canvas read on the loaded image throws
      img.onload = function () { resolve(img); };
      img.onerror = function () { resolve(null); };
      img.src = url;
    });
  }

  function sample(img) {
    try {
      var w = 48, h = 48;
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      var patchSize = 7;
      var patches = [
        [0, 0], [w - patchSize, 0], [0, h - patchSize], [w - patchSize, h - patchSize],
        [Math.floor(w / 2 - patchSize / 2), 0]
      ];
      var sum = 0, sumSq = 0, n = 0;
      patches.forEach(function (xy) {
        var d = ctx.getImageData(xy[0], xy[1], patchSize, patchSize).data;
        for (var i = 0; i < d.length; i += 4) {
          var lum = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
          sum += lum; sumSq += lum * lum; n++;
        }
      });
      var mean = sum / n;
      var variance = sumSq / n - mean * mean;
      return { brightness: mean, variance: variance };
    } catch (e) {
      return null; // canvas read blocked (CORS, decode failure) — treat as no signal
    }
  }

  function score(stats) {
    if (!stats) return -1;
    // Reward corner brightness above a mid-grey floor (a seamless studio backdrop
    // reads bright and even; a lifestyle photo's corners are whatever the scene
    // behind the model happens to be — skin, fabric, foliage, floor — rarely this
    // uniformly bright), penalize variance (texture/pattern/edges in the corners).
    var brightnessScore = Math.max(0, stats.brightness - 195) * 1.8;
    var variancePenalty = Math.min(stats.variance / 5, 90);
    return brightnessScore - variancePenalty;
  }

  // Calibrated against this catalog's own real Oscar4Fox photos, not guessed: a
  // genuine seamless-backdrop studio shot (corners sampled ~230-240 brightness,
  // JPEG compression keeps it just short of pure white) scored 27-62 across the
  // four products checked; every lifestyle/worn shot in the same sets scored
  // -90 to -4. 20 sits with real margin below every real studio shot seen and
  // real margin above every real lifestyle shot seen — the original 55 was a
  // guess made without this data and never once cleared, silently disabling
  // the picker on every product.
  var WIN_THRESHOLD = 20; // a score below this is too ambiguous to override the merchant's own choice

  function choose(product, onBetter) {
    var id = product && product.id;
    var fallback = (product && product.photo) || '';
    if (!id) return fallback;
    if (id in cache) return cache[id];
    if (!product.images || product.images.length < 2 || pending[id]) return fallback;

    pending[id] = true;
    var urls = product.images.slice(0, 6);
    Promise.all(urls.map(loadImg)).then(function (imgs) {
      pending[id] = false;
      var bestScore = -1, bestUrl = fallback;
      imgs.forEach(function (img, i) {
        if (!img) return;
        var s = score(sample(img));
        if (s > bestScore) { bestScore = s; bestUrl = urls[i]; }
      });
      var chosen = bestScore >= WIN_THRESHOLD ? bestUrl : fallback;
      // Cache the outcome either way — an ambiguous result (chosen === fallback)
      // is still a real answer, not "not scored yet"; see the top-of-file note
      // on why leaving this unset was a genuine runaway-loop bug.
      cache[id] = chosen;
      if (chosen !== fallback && onBetter) onBetter(chosen);
    });

    return fallback;
  }

  window.SATPhotoPicker = { choose: choose };
})();
